// components/GlobalSearch/GlobalSearch.tsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, Loader2, User, ShoppingCart, RefreshCw, ArrowRight, Phone, Mail, ClipboardList, CheckCircle } from 'lucide-react';
import { cn } from '@/utils/cn';
import { SearchResultItem, SearchResults, SearchCategory, LeadDetailData } from '@/types/globalSearch';
import api from '@/utils/api';
import { format, parseISO, isToday, isPast } from 'date-fns';
import { claimsService } from '@/services/claimsService';
import { showToast } from '@/components/ui/Toast';

interface GlobalSearchProps {
  className?: string;
  variant?: 'icon' | 'bar';
}

const DEBOUNCE_MS = 300;
const PHONE_LENGTH = 10;

const categoryConfig: Record<SearchCategory, { label: string; icon: React.ReactNode; color: string }> = {
  leads: { label: 'ABC Leads', icon: <ShoppingCart size={16} />, color: 'bg-blue-100 text-blue-700' },
  reorder: { label: 'Reorder Leads', icon: <RefreshCw size={16} />, color: 'bg-green-100 text-green-700' },
  orders: { label: 'Orders', icon: <ShoppingCart size={16} />, color: 'bg-purple-100 text-purple-700' },
  users: { label: 'Users', icon: <User size={16} />, color: 'bg-orange-100 text-orange-700' },
};


const GlobalSearch: React.FC<GlobalSearchProps> = ({ className, variant = 'icon' }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResults | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [error, setError] = useState<string | null>(null);

  // Claim state: uuid -> 'idle' | 'claiming' | 'reason' | 'done'
  const [claimState, setClaimState] = useState<Record<string, 'idle' | 'claiming' | 'reason' | 'done'>>({});
  const [claimReason, setClaimReason] = useState<Record<string, string>>({});

  const handleClaim = async (leadUuid: string, reason?: string) => {
    setClaimState(prev => ({ ...prev, [leadUuid]: 'claiming' }));
    try {
      const res: any = await claimsService.submitClaim(leadUuid, reason);
      if (res.success) {
        showToast('Claim request submitted successfully', 'success');
        setClaimState(prev => ({ ...prev, [leadUuid]: 'done' }));
      } else {
        showToast(res.message || 'Failed to submit claim', 'error');
        setClaimState(prev => ({ ...prev, [leadUuid]: 'idle' }));
      }
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to submit claim';
      showToast(msg, 'error');
      setClaimState(prev => ({ ...prev, [leadUuid]: 'idle' }));
    }
  };

  // Get flattened results for keyboard navigation
  const flatResults = React.useMemo(() => {
    if (!results) return [];
    return [...results.leads, ...results.reorder];
  }, [results]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Handle keyboard shortcut (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        setIsOpen(true);
      }
      if (event.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Extract only digits from input
  const getDigitsOnly = (value: string) => value.replace(/\D/g, '');

  // Search function
  const performSearch = useCallback(async (phone: string) => {
    const digits = getDigitsOnly(phone);
    if (digits.length !== PHONE_LENGTH) {
      setResults(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await api.post<SearchResults>('/global-search', { phone: digits, limit: 10 });

      if (response.success && response.data) {
        setResults(response.data);
      } else {
        setError('No results found');
        setResults({ leads: [], reorder: [], total: 0 });
      }
    } catch (err) {
      console.error('Search error:', err);
      setError('Search failed. Please try again.');
      setResults(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounced search - only triggers when 10 digits entered
  const handleQueryChange = useCallback((value: string) => {
    // Only allow digits
    const digitsOnly = getDigitsOnly(value);
    // Limit to 10 digits
    const limitedDigits = digitsOnly.slice(0, PHONE_LENGTH);
    setQuery(limitedDigits);
    setSelectedIndex(-1);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (limitedDigits.length === PHONE_LENGTH) {
      setIsLoading(true);
      debounceRef.current = setTimeout(() => {
        performSearch(limitedDigits);
      }, DEBOUNCE_MS);
    } else {
      setResults(null);
      setIsLoading(false);
    }
  }, [performSearch]);

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (flatResults.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex((prev) => (prev < flatResults.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : flatResults.length - 1));
        break;
      case 'Enter':
        event.preventDefault();
        if (selectedIndex >= 0 && flatResults[selectedIndex]) {
          handleResultClick(flatResults[selectedIndex]);
        }
        break;
    }
  };

  // Handle result click — open lead detail page in a new tab
  const handleResultClick = (result: SearchResultItem) => {
    const data = result.data as LeadDetailData | undefined;
    if (!data?.uuid) return;

    setIsOpen(false);
    setQuery('');
    setResults(null);
    setError(null);
    setSelectedIndex(-1);

    window.open(`/lead/${data.uuid}`, '_blank');
  };

  // Close modal and reset state
  const handleClose = () => {
    setIsOpen(false);
    setQuery('');
    setResults(null);
    setError(null);
    setSelectedIndex(-1);
  };

  // Format date helper
  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return null;
    try {
      const date = parseISO(dateStr);
      if (isToday(date)) return 'Today';
      return format(date, 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  // Format currency

  // Render result item with comprehensive details
  const renderResultItem = (item: SearchResultItem, index: number) => {
    const isSelected = index === selectedIndex;
    const config = categoryConfig[item.type];
    const data = item.data as LeadDetailData | undefined;

    // Check if reorder is due/overdue
    const isReorderDue = data?.next_reorder_date && (isToday(parseISO(data.next_reorder_date)) || isPast(parseISO(data.next_reorder_date)));

    return (
      <div
        key={`${item.type}-${item.uuid}`}
        className={cn(
          'border-b border-gray-100 last:border-b-0 transition-colors',
          isSelected ? 'bg-brandColor/5' : 'hover:bg-gray-50'
        )}
        onMouseEnter={() => setSelectedIndex(index)}
      >
        {/* Main clickable header */}
        <button
          onClick={() => handleResultClick(item)}
          className="w-full flex items-start gap-4 px-6 py-4 text-left"
        >
          {/* Category Icon */}
          <div className={cn('flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0 mt-1', config.color)}>
            {config.icon}
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Row 1: Name, Badges, Status */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900 text-lg">{item.title || 'Unknown'}</span>
              {isReorderDue && (
                <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-red-100 text-red-700">
                  Due {formatDate(data?.next_reorder_date)}
                </span>
              )}
            </div>

            {/* Row 2: Phone, Email, Source */}
            <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
              {data?.phone && (
                <span className="flex items-center gap-1">
                  <Phone size={14} className="text-gray-400" />
                  {data.phone}
                </span>
              )}
              {data?.email && (
                <span className="flex items-center gap-1 truncate">
                  <Mail size={14} className="text-gray-400" />
                  {data.email}
                </span>
              )}
              {data?.source_display && (
                <span className="text-gray-400">• {data.source_display}</span>
              )}
            </div>

            {/* Row 3: Assignment & Disposition */}
            <div className="flex items-center gap-4 mt-2 text-sm">
              {data?.assigned_to ? (
                <span className="flex items-center gap-1 text-blue-600">
                  <User size={14} />
                  {data.assigned_to.user_name}
                  {data.assigned_to.assignment_type === 'reorder' && (
                    <span className="text-xs text-gray-400">(reorder team)</span>
                  )}
                </span>
              ) : (
                <span className="text-gray-400">Unassigned</span>
              )}
              {(data?.disposition_l1_display || data?.disposition_l2_display) && (
                <span className="text-gray-500">
                  {[data.disposition_l1_display, data.disposition_l2_display, data.disposition_l3_display]
                    .filter(Boolean)
                    .join(' → ')}
                </span>
              )}
            </div>


          </div>

          {/* Arrow indicator */}
          <ArrowRight
            size={20}
            className={cn(
              'text-gray-400 flex-shrink-0 mt-2 transition-opacity',
              isSelected ? 'opacity-100' : 'opacity-0'
            )}
          />
        </button>

        {/* Claim section — outside the main button */}
        {item.uuid && (
          <div className="px-6 pb-3 flex items-start gap-2">
            {claimState[item.uuid] === 'done' ? (
              <span className="flex items-center gap-1.5 text-xs text-green-600 font-medium">
                <CheckCircle className="h-3.5 w-3.5" /> Claim request sent
              </span>
            ) : claimState[item.uuid] === 'reason' ? (
              <div className="flex items-center gap-2 w-full">
                <input
                  type="text"
                  value={claimReason[item.uuid] || ''}
                  onChange={e => setClaimReason(prev => ({ ...prev, [item.uuid]: e.target.value }))}
                  placeholder="Reason (optional)"
                  className="flex-1 px-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-brandColor"
                  autoFocus
                />
                <button
                  onClick={() => handleClaim(item.uuid, claimReason[item.uuid])}
                  className="px-3 py-1.5 bg-brandColor text-white text-xs font-medium rounded-lg hover:opacity-90 transition-opacity"
                >
                  Submit
                </button>
                <button
                  onClick={() => setClaimState(prev => ({ ...prev, [item.uuid]: 'idle' }))}
                  className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={e => {
                  e.stopPropagation();
                  setClaimState(prev => ({ ...prev, [item.uuid]: 'reason' }));
                }}
                disabled={claimState[item.uuid] === 'claiming'}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-brandColor border border-brandColor rounded-lg hover:bg-brandColor hover:text-white transition-colors disabled:opacity-50"
              >
                <ClipboardList className="h-3.5 w-3.5" />
                {claimState[item.uuid] === 'claiming' ? 'Claiming...' : 'Claim Lead'}
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  // Render category section
  const renderCategorySection = (category: SearchCategory, items: SearchResultItem[]) => {
    if (items.length === 0) return null;

    const config = categoryConfig[category];
    const startIndex = category === 'leads' ? 0 : (results?.leads.length || 0);

    return (
      <div key={category}>
        <div className="px-6 py-3 bg-gray-100">
          <span className="text-sm font-semibold text-gray-600 uppercase tracking-wider">
            {config.label}
          </span>
        </div>
        {items.map((item, idx) => renderResultItem(item, startIndex + idx))}
      </div>
    );
  };

  return (
    <>
      {variant === 'bar' ? (
        /* Search bar — visible input-style trigger */
        <button
          onClick={() => setIsOpen(true)}
          className={cn(
            'flex items-center gap-2.5 h-8 px-3 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 hover:border-gray-300 transition-colors text-left',
            'w-52',
            className
          )}
          title="Search (⌘K)"
        >
          <Search size={14} className="text-gray-400 shrink-0" />
          <span className="text-sm text-gray-400 flex-1">Search by phone…</span>
          <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-mono text-gray-400 bg-white border border-gray-200 rounded">
            ⌘K
          </kbd>
        </button>
      ) : (
        /* Icon-only button (legacy / navbar use) */
        <button
          onClick={() => setIsOpen(true)}
          className={cn(
            'flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200',
            'bg-white/10 hover:bg-white/20 text-white/70 hover:text-white',
            className
          )}
          title="Search (⌘K)"
        >
          <Search size={20} />
        </button>
      )}

      {/* Search Modal - rendered via portal to document.body */}
      {isOpen && createPortal(
        <div
          className="fixed inset-0 flex justify-center bg-black/50"
          onClick={handleClose}
          style={{ zIndex: 99999 }}
        >
          <div className="w-2/3 bg-white flex flex-col shadow-2xl mb-8 rounded-b-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
          {/* Search Header */}
          <div className="flex items-center gap-4 px-6 py-5 bg-gray-50">
            <Search size={24} className="text-gray-400 flex-shrink-0" />
            <input
              ref={inputRef}
              type="tel"
              value={query}
              onChange={(e) => handleQueryChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter 10 digit phone number..."
              className="flex-1 text-xl bg-transparent border-none outline-none text-gray-900 placeholder-gray-400 font-medium tracking-wide"
              autoComplete="off"
              maxLength={10}
            />
            {query && (
              <span className={cn(
                "text-sm font-medium",
                query.length === PHONE_LENGTH ? "text-green-600" : "text-gray-400"
              )}>
                {query.length}/{PHONE_LENGTH}
              </span>
            )}
            {isLoading && <Loader2 size={24} className="text-brandColor animate-spin flex-shrink-0" />}
            {query && !isLoading && (
              <button
                onClick={() => { setQuery(''); setResults(null); inputRef.current?.focus(); }}
                className="text-gray-400 hover:text-gray-600 flex-shrink-0 p-1"
              >
                <X size={24} />
              </button>
            )}
            <button
              onClick={handleClose}
              className="ml-2 px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>

          {/* Results Area */}
          <div className="flex-1 overflow-y-auto">
            {/* Loading State */}
            {isLoading && !results && (
              <div className="flex items-center justify-center py-20 text-gray-500">
                <Loader2 size={32} className="animate-spin mr-3" />
                <span className="text-xl">Searching...</span>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="flex items-center justify-center py-20 text-red-500">
                <span className="text-xl">{error}</span>
              </div>
            )}

            {/* Results */}
            {results && !isLoading && (
              <>
                {results.total === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20 text-gray-500">
                    <Search size={64} className="mb-4 opacity-30" />
                    <p className="text-xl">No results found for "{query}"</p>
                    <p className="text-base text-gray-400 mt-2">Try a different phone number</p>
                  </div>
                ) : (
                  <>
                    {renderCategorySection('leads', results.leads)}
                    {renderCategorySection('reorder', results.reorder)}
                  </>
                )}
              </>
            )}

            {/* Empty Query Hint */}
            {query.length < PHONE_LENGTH && !results && !isLoading && (
              <div className="flex flex-col items-center justify-center py-20 text-gray-500">
                <Search size={64} className="mb-4 opacity-30" />
                <p className="text-xl">Search by Phone Number</p>
                <p className="text-base text-gray-400 mt-2">
                  Enter a 10 digit phone number to search
                </p>
                {query.length > 0 && (
                  <p className="text-sm text-gray-400 mt-4">
                    {PHONE_LENGTH - query.length} more digit{PHONE_LENGTH - query.length !== 1 ? 's' : ''} needed
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          {results && results.total > 0 && (
            <div className="px-6 py-4 bg-gray-50 flex items-center justify-between">
              <span className="text-sm text-gray-500">
                {results.total} result{results.total !== 1 ? 's' : ''} found
              </span>
              <div className="flex items-center gap-4 text-sm text-gray-400">
                <span className="flex items-center gap-1">
                  <kbd className="px-2 py-1 bg-gray-200 rounded font-mono">↑</kbd>
                  <kbd className="px-2 py-1 bg-gray-200 rounded font-mono">↓</kbd>
                  <span className="ml-1">Navigate</span>
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-2 py-1 bg-gray-200 rounded font-mono">↵</kbd>
                  <span className="ml-1">Select</span>
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-2 py-1 bg-gray-200 rounded font-mono">Esc</kbd>
                  <span className="ml-1">Close</span>
                </span>
              </div>
            </div>
          )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default GlobalSearch;
